import { isEHRAppointment } from '@/components/layout/Navigation/sessions/side-nav-list/integration/hooks/useRetrieveAppointmentOrSchedule';
import {
  type EHRAppointment,
  type ScheduleItem,
} from '@heidi/integrations/ehr/contracts';

type HeidiSession = {
  session_id?: string | null;
  ehr_appt_id?: string | null;
};

/**
 * Checks whether an EHR appointment is already linked to a Heidi session.
 *
 * For legacy EHR appointments, matching is by `session_id`.
 * For integration schedule items (e.g. Genie), matching tries `appointment.session.session_id`
 * first, then falls back to `provider_details.unique_identifier` vs `ehr_appt_id`.
 *
 * The fallback is needed because desktop integrations like Genie always return `session: null`
 * from the PMS. The backend stores `provider_details.unique_identifier` as `ehr_appt_id`
 * on the session, so we can match on that field instead.
 */
export function isEhrAppointmentLinked(
  item: EHRAppointment | ScheduleItem,
  heidiSessions: HeidiSession[]
): boolean {
  // Legacy EHR appointment
  if (isEHRAppointment(item)) {
    const { session_id } = item;
    if (!session_id) return false;
    return heidiSessions.some((session) => session.session_id === session_id);
  }

  // Integration schedule item (e.g. Genie)
  const sessionId = item.appointment.session?.session_id;
  if (sessionId) {
    return heidiSessions.some((session) => session.session_id === sessionId);
  }

  // Fallback: match by provider unique identifier against ehr_appt_id
  const providerUniqueId = item.appointment.provider_details?.unique_identifier;
  if (providerUniqueId) {
    return heidiSessions.some((session) => session.ehr_appt_id === providerUniqueId);
  }

  return false;
}
